import React, { useState, useEffect, useRef } from 'react';
import { X, Send, BrainCircuit, User } from 'lucide-react';
import { getFunctions, httpsCallable } from "firebase/functions";
import { getApp } from "firebase/app";

// 1. Recibimos generalContext como prop (asumiendo que tu App.js se lo pasa cuando lo abre)
const GeneralAiChatModal = ({ isOpen, onClose, user, generalContext }) => {
    const functions = getFunctions(getApp(), 'us-central1');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            setMessages([
                { sender: 'ai', text: `¡Hola ${user?.displayName || user?.email || ''}! Soy el asistente general. ¿Qué te gustaría saber?` }
            ]);
        }
    }, [isOpen, user]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        
        const messageText = input.trim(); 
        if (!messageText || isLoading) return;

        setMessages(prev => [...prev, { sender: 'user', text: messageText }]);
        setInput('');
        setIsLoading(true);

        try {
            const chatAiGeneral = httpsCallable(functions, 'chatAiGeneral');
            
            // 2. Le inyectamos el contexto de fondo a la pregunta del usuario
            const enrichedPrompt = `Contexto del sistema: ${generalContext}. Pregunta del usuario: ${messageText}`;
            
            const result = await chatAiGeneral({ userMessage: enrichedPrompt });
            
            const aiResponse = { sender: 'ai', text: result.data.response || "No he podido procesar tu solicitud." };
            setMessages(prev => [...prev, aiResponse]);

        } catch (error) {
            console.error("Error AI:", error);
            setMessages(prev => [...prev, { sender: 'ai', text: `Error de conexión: ${error.message}` }]);
        } finally {
            setIsLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
            <div className="bg-white rounded-lg shadow-2xl w-full max-w-2xl h-[70vh] flex flex-col">
                <header className="p-4 border-b flex justify-between items-center bg-gray-50 rounded-t-lg">
                    <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        <BrainCircuit size={20} className="text-purple-600"/>
                        Asistente General de Producción
                    </h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-800"><X size={24} /></button>
                </header>

                <main className="flex-grow p-4 overflow-y-auto bg-gray-100">
                    <div className="space-y-4">
                        {messages.map((msg, index) => (
                            <div key={index} className={`flex items-start gap-3 ${msg.sender === 'user' ? 'justify-end' : ''}`}>
                                {msg.sender === 'ai' && <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white flex-shrink-0"><BrainCircuit size={18}/></div>}
                                <div className={`p-3 rounded-lg max-w-lg ${msg.sender === 'user' ? 'bg-blue-500 text-white' : 'bg-white text-gray-800'}`}>
                                    <p className="text-sm" style={{ whiteSpace: 'pre-wrap' }}>{msg.text}</p>
                                </div>
                                {msg.sender === 'user' && <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center text-white flex-shrink-0"><User size={18}/></div>}
                            </div>
                        ))}
                         {isLoading && (
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center text-white flex-shrink-0"><BrainCircuit size={18}/></div>
                                <div className="p-3 rounded-lg bg-white text-gray-800">
                                    <p className="text-sm italic">Pensando...</p>
                                </div>
                            </div>
                        )}
                        <div ref={messagesEndRef} />
                    </div>
                </main>

                <footer className="p-4 border-t bg-white rounded-b-lg">
                    <form onSubmit={handleSend} className="flex items-center gap-2">
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            placeholder="Ej: ¿Qué trabajos están detenidos y por qué?"
                            className="w-full p-2 border border-gray-300 rounded-lg"
                            disabled={isLoading}
                        />
                        <button type="submit" className="p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400" disabled={isLoading || !input.trim()}>
                            <Send size={20}/>
                        </button>
                    </form>
                </footer>
            </div>
        </div>
    );
};

export default GeneralAiChatModal;